import React from 'react';
import { Button, Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { ProductImage } from '../Helpers/ProductImage';
import { useLocalization } from '../Helpers/useLocalization';

interface IProductImageModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // The full image set. A single-image page (e.g. a bundle) just passes one.
    images: string[];
    // Index of the image being viewed. Controlled by the page so the gallery selection and the
    // modal stay in step when the shopper pages through images in here.
    index: number;
    onIndexChange: (index: number) => void;
    title?: string;
}

// Full-size image viewer, opened by clicking the main image on the product / bundle pages. Pages
// through the gallery when there is more than one image, mirroring the thumbnail strip's arrows.
const ProductImageModal: React.FC<IProductImageModalProps> = (props) => {
    const L = useLocalization();
    const count = props.images.length;
    const index = Math.min(Math.max(props.index, 0), Math.max(count - 1, 0));
    // Wrap around, so paging never dead-ends on the first/last image.
    const step = (by: number) => props.onIndexChange((index + by + count) % count);

    // Left/right keys page the viewer, matching the on-screen arrows. Esc is handled by Dialog.
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (count < 2) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    };

    return (
        <Dialog open={props.open} onOpenChange={(_, d) => props.onOpenChange(d.open)}>
            <DialogSurface
                onClick={e => e.stopPropagation()}
                onKeyDown={onKeyDown}
                style={{ maxWidth: '1000px', width: '90vw' }}
            >
                <DialogBody>
                    <DialogTitle>{props.title ?? ''}</DialogTitle>
                    <DialogContent>
                        <div className='product-image-modal'>
                            {count > 1 && (
                                <Button
                                    className='card-scroller-arrow card-scroller-arrow-left'
                                    shape='circular'
                                    appearance='subtle'
                                    icon={<ChevronLeftRegular />}
                                    aria-label='Previous image'
                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                    onClick={() => step(-1)}
                                />
                            )}
                            <ProductImage
                                src={props.images[index]}
                                alt={props.title ?? ''}
                                fit='contain'
                                className='product-image-modal-img'
                            />
                            {count > 1 && (
                                <Button
                                    className='card-scroller-arrow card-scroller-arrow-right'
                                    shape='circular'
                                    appearance='subtle'
                                    icon={<ChevronRightRegular />}
                                    aria-label='Next image'
                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                    onClick={() => step(1)}
                                />
                            )}
                        </div>
                        {count > 1 && (
                            <div className='product-image-modal-count'>{index + 1} / {count}</div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance='secondary' onClick={() => props.onOpenChange(false)}>{L('localization_common_close')}</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default ProductImageModal;
